<template>
  <ReportCard :title="$t('report.members_age_title')">
    <template #header>
      <button @click.stop="showAgeInfo = true"
        class="flex items-center justify-center w-4 h-4 text-gray-400 hover:text-blue-500 transition-colors"
        :title="$t('report.members_age_info.title')">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
      </button>
    </template>
    <div class="relative">
      <div ref="chartEl" class="w-full" style="height: 280px"></div>
      <div v-if="loading"
        class="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-lg transition-opacity">
        <Loading />
      </div>
    </div>
    <p v-if="ageUnknownPct" class="text-[10px] text-muted-foreground/60 mt-2 ml-1">
      {{ $t('report.members_age_unknown_pct', { pct: ageUnknownPct }) }}
    </p>
  </ReportCard>

  <BottomSheet v-model:is-open="showAgeInfo" variant="sidebar">
    <MembersAgeInfoSheet />
  </BottomSheet>
</template>

<script setup lang="ts">
import { computed, ref, shallowRef, watch, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import * as echarts from 'echarts'
import type { MembersStatsResponse } from '../../responses'
import ReportCard from '../ReportCard.vue'
import Loading from '../Loading.vue'
import BottomSheet from '../BottomSheet.vue'
import MembersAgeInfoSheet from '../MembersAgeInfoSheet.vue'
import { useIsDarkMode } from '../../composables/useIsDarkMode'

const props = defineProps<{
  response: MembersStatsResponse | null
  loading: boolean
}>()

const { t } = useI18n()

const chartEl = ref<HTMLElement>()
const chartInstance = shallowRef<echarts.ECharts>()
const showAgeInfo = ref(false)

const isDark = useIsDarkMode()

const AGE_ORDER = ['under_12', '12-17', '18-25', '26-35', '36-50', '50+']
const AGE_COLORS = ['#67E8F9', '#22D3EE', '#06B6D4', '#0891B2', '#0E7490', '#155E75']

function ageLabel(key: string): string {
  return t(`report.members_age_${key}`)
}

const ageUnknownPct = computed(() => {
  if (!props.response?.data) return null
  const all = props.response.data
  const total = all.reduce((s, d) => s + (d.metrics.count ?? 0), 0)
  if (total === 0) return null
  const unknown = all.find(d => d.dimensions.age_range === 'unknown')?.metrics.count ?? 0
  if (unknown === 0) return null
  return Math.round((unknown / total) * 100)
})

function buildChart() {
  if (!props.response?.data) return
  const dark = isDark.value

  const dataMap = new Map<string, number>()
  for (const point of props.response.data) {
    const key = point.dimensions.age_range
    if (key) dataMap.set(key, (dataMap.get(key) ?? 0) + (point.metrics.count ?? 0))
  }

  const categoryKeys = AGE_ORDER.filter(k => dataMap.has(k))
  const categories = categoryKeys.map(k => ageLabel(k))
  const values = categoryKeys.map(k => dataMap.get(k) ?? 0)
  const total = values.reduce((s, v) => s + v, 0)

  const options: echarts.EChartsOption = {
    grid: { left: '2%', right: '8%', bottom: '4%', top: '4%', containLabel: true },
    xAxis: {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: dark ? 'rgba(255,255,255,0.06)' : 'rgba(229,231,235,0.3)', type: 'dashed' } },
      axisLabel: { color: dark ? 'rgba(255,255,255,0.5)' : 'rgba(75,85,99,0.7)', fontSize: 10 },
    },
    yAxis: {
      type: 'category',
      data: categories,
      axisLine: { lineStyle: { color: dark ? 'rgba(255,255,255,0.12)' : 'rgba(156,163,175,0.25)' } },
      axisTick: { show: false },
      axisLabel: { color: dark ? 'rgba(255,255,255,0.6)' : 'rgba(75,85,99,0.8)', fontSize: 11 },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: dark ? 'rgba(40,39,39,0.95)' : 'rgba(255,255,255,0.95)',
      borderColor: dark ? 'rgba(255,255,255,0.1)' : 'rgba(229,231,235,0.8)',
      borderWidth: 1, borderRadius: 8, confine: true,
      textStyle: { color: dark ? 'rgba(255,255,255,0.9)' : 'rgba(17,24,39,0.9)', fontSize: 12 },
      formatter: (params: unknown) => {
        if (!Array.isArray(params) || params.length === 0) return ''
        const p = params[0] as { axisValue: string; value: number }
        const pct = total > 0 ? ((p.value / total) * 100).toFixed(1) : '0.0'
        return `<div style="font-weight:600;margin-bottom:4px">${p.axisValue}</div><div style="display:flex;align-items:center;gap:6px"><span style="font-weight:700">${p.value}</span><span style="color:rgba(128,128,128,0.8)">(${pct}%)</span></div>`
      },
    },
    series: [{
      type: 'bar',
      data: values.map((v, i) => ({
        value: v,
        itemStyle: { color: AGE_COLORS[AGE_ORDER.indexOf(categoryKeys[i])] ?? '#06C8FF' },
      })),
      barMaxWidth: 24,
      label: {
        show: true, position: 'right', fontSize: 10, fontWeight: 'bold',
        color: dark ? 'rgba(255,255,255,0.6)' : 'rgba(75,85,99,0.7)',
        formatter: (p: unknown) => {
          const val = (p as { value: number }).value
          const pct = total > 0 ? ((val / total) * 100).toFixed(0) : '0'
          return `${val} (${pct}%)`
        },
      },
    }],
  }

  if (chartInstance.value) {
    chartInstance.value.setOption(options, true)
  } else if (chartEl.value) {
    chartInstance.value = echarts.init(chartEl.value)
    chartInstance.value.setOption(options)
  }
}

function refreshChart() {
  buildChart()
}

watch(() => props.response, async () => {
  await nextTick()
  buildChart()
})

watch(isDark, () => buildChart())

watch(chartEl, async (el) => {
  if (el && !chartInstance.value) {
    await nextTick()
    setTimeout(buildChart, 300)
  }
}, { immediate: true })

onBeforeUnmount(() => {
  chartInstance.value?.dispose()
})

defineExpose({ refreshChart })
</script>
